import { TelemetryEvent, DataField, DataFieldType } from './DataModels';

export namespace TelemetryEventValidator {
  const INT64_MAX = 9007199254740991;
  const INT64_MIN = -INT64_MAX;

  // Starts with A-Z, and then contains only alphanumerics
  const StartsWithCapitalRegex = /^[A-Z][a-zA-Z0-9]*$/;

  // Contains alphanumeric or _ or .
  const AlphanumericRegex = /^[a-zA-Z0-9_\.]*$/;

  export function validateTelemetryEvent(telemetryEvent: TelemetryEvent) {
    if (!isEventNameValid(telemetryEvent.eventName)) {
      throw new Error('Invalid eventName');
    }

    if (telemetryEvent.eventContract && !isEventContractValid(telemetryEvent.eventContract)) {
      throw new Error('Invalid eventContract');
    }

    if (telemetryEvent.dataFields != null) {
      for (let i = 0; i < telemetryEvent.dataFields!.length; i++) {
        validateDataField(telemetryEvent.dataFields![i]);
      }
    }
  }

  function isNamespaceValid(eventNamePieces: string[] | undefined): boolean {
    // Min 3 pieces: Office.(Namespace).EventName)
    return !!eventNamePieces && eventNamePieces.length >= 3 && eventNamePieces[0] === 'Office';
  }

  function isEventNodeValid(eventNode: string | undefined): boolean {
    return eventNode !== undefined && StartsWithCapitalRegex.test(eventNode);
  }

  function isEventNameValid(eventName: string | undefined): boolean {
    let maxEventNameLength = 98; // Client Code = 100 limit; Aria Compact SDK = 98 limit.
    if (!eventName || eventName.length > maxEventNameLength) {
      return false;
    }

    let eventNamePieces = eventName.split('.');
    let eventNodeName = eventNamePieces[eventNamePieces.length - 1];
    return isNamespaceValid(eventNamePieces) && isEventNodeValid(eventNodeName);
  }

  function isEventContractValid(eventContract: { name: string; dataFields: DataField[] }): boolean {
    return isNameValid(eventContract.name);
  }

  function isDataFieldNameValid(dataFieldName: string | undefined): boolean {
    let maxDataFieldNameLength = 100;
    let dataFieldPrefixLength = 5;

    return !!dataFieldName && isNameValid(dataFieldName) && dataFieldName.length + dataFieldPrefixLength < maxDataFieldNameLength;
  }

  function isNameValid(name: string | undefined): boolean {
    return name !== undefined && AlphanumericRegex.test(name);
  }

  function validateDataField(dataField: DataField): void {
    if (!isDataFieldNameValid(dataField.name)) {
      throw new Error('Invalid dataField name');
    }

    if (dataField.dataType === DataFieldType.Int64) {
      validateInt(dataField.value);
    }
  }

  export function validateInt(value: any): void {
    if (typeof value !== 'number' || !isFinite(value) || Math.floor(value) !== value || value < INT64_MIN || value > INT64_MAX) {
      throw new Error(`Invalid integer ${JSON.stringify(value)}`);
    }
  }
}
